import Rollbar from 'rollbar';
import { AppStatus } from '@/app/utils/constants/AppStatus';
import EventBus from '@/app/eventBus';
import accountApi from '@/app/services/api/account';


// Setup Rollbar
// Can use Rollbar in components by this.vm.$rollbar.error('Some Error')
const rollbar = new Rollbar({
  accessToken: `${process.env.VUE_APP_ROLLBAR_CLIENT_TOKEN}`,
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: `${process.env.VUE_APP_ENV}`,
  },
});


const mutations = {
  setApiKey(state, apiKey) {
    state.apiKey = apiKey;
  },
  setAppPermissions(state, appPermissions) {
    state.appPermissions = appPermissions;
  },
  setAccounts(state, accounts) {
    state.accounts = [...accounts];
  },
  setActiveAccount(state, activeAccount) {
    state.activeAccount = activeAccount;
  },
  SET_ACTIVE_ACCOUNT_USER(state, activeAccountUser) {
    state.activeAccountUser = activeAccountUser;
  },
  // New Mutations
  SET_ACCOUNT(state, account) {
    state.account = account;
  },
  SET_ACCOUNT_NAME(state, name) {
    state.account.name = name;
  },
  SET_ACCOUNT_USER(state, accountUser) {
    state.accountUser = accountUser;
  },
  SET_APP_PERMISSIONS(state, permissions) {
    state.appPermissions = permissions;
  },
  SET_BILLING_DETAILS(state, billingDetails) {
    state.account.billingDetails = billingDetails;
  },
  UPDATE_ACTIVE_ACCOUNT(state, activeAccount) {
    const currentActiveAccountIdx = state.accounts
      .findIndex(act => act.uuid === activeAccount.uuid);
    state.accounts[currentActiveAccountIdx] = activeAccount;
    state.activeAccount = activeAccount;
  },
  SET_STRIPE_API_KEY(state, stripeApiKey) {
    state.apiKey = stripeApiKey;
  },
  SET_BORG_API_KEY(state, borgApiKey) {
    state.borgApiKey = borgApiKey;
  },
  CLEAR_ACCOUNT(state) {
    state.apiKey = null;
    state.borgApiKey = null;
    state.account = null;
    state.accountUser = null;
    state.appPermissions = [];
  },
};

const actions = {
  setApiKey({ commit }, apiKey) {
    commit('setApiKey', apiKey);
  },
  setBorgApiKey({ commit }, accountUUID) {
    return accountApi.getBorgApiKey(accountUUID)
      .then((res) => {
        commit('SET_BORG_API_KEY', res.data.data.APIKey);
        return res;
      });
  },
  createAccount(_, data) {
    return accountApi.createAccount(data);
  },
  addAppPermissions({ commit }, { permissions }) {
    const updatedAppPermissions = permissions.map((permission) => {
      // Handle use cases where the APP PERMISSION is set to INACTIVE due to cancel or past due
      const status = permission.status.includes('ACTIVE') ? 'ACTIVE' : permission.status;
      return {
        ...permission,
        status,
      };
    });
    commit('setAppPermissions', updatedAppPermissions);
  },
  updateBillingDetails({ commit }, billingDetails) {
    commit('SET_BILLING_DETAILS', billingDetails);
  },
  clearAccounts: {
    root: true,
    handler: function clearAccounts({ commit }) {
      commit('CLEAR_ACCOUNT');
    },
  },
  setNotifications({ commit }, accountUUID) {
    return accountApi.getAccount(accountUUID)
      .then((res) => {
        const { notifications } = res.data.data;
        commit('SET_ACCOUNT', res.data.data);
        if (notifications && notifications.length > 0) {
          notifications.forEach((notification) => {
            EventBus.$emit('addNotification', {
              title: 'Account Notification',
              icon: 'warn',
              message: notification.data.message,
              uuid: notification.uuid,
              isReadable: true,
            });
          });
        }
      });
  },
  refreshAccounts({ dispatch, commit }) {
    return accountApi
      .getUserAccount()
      .then((accountUserResponse) => {
        const {
          account,
          permissions,
          user,
          ...data
        } = accountUserResponse.data.data;
        if (!account) {
          const err = new Error('No Account');
          err.response = accountUserResponse;
          throw err;
        }
        commit('SET_ACCOUNT', account);
        dispatch('addAppPermissions', { ...data, permissions });
        commit('SET_ACCOUNT_USER', { ...data, ...user });
        dispatch('setNotifications', account.uuid);
        dispatch('setActiveApps', null, { root: true });
        return dispatch('setBorgApiKey', account.uuid);
      })
      .catch((err) => {
        rollbar.error('refreshAccounts::error', {
          status_code: 404,
          err,
        });
        throw err;
      });
  },
  updateActiveAccount({ commit }, activeAccount) {
    return accountApi.updateAccount(activeAccount)
      .then((response) => {
        if (response.status === 201) {
          const updatedActiveAccount = response.data.data;
          commit('UPDATE_ACTIVE_ACCOUNT', updatedActiveAccount);
          return response;
        }
        // Unhandled status
        throw new Error('Failed to update active account!');
      })
      .catch((err) => {
        throw err;
      });
  },
  updateActiveAccountName({ commit }, { name }) {
    commit('SET_ACCOUNT_NAME', name);
  },
};

const getters = {
  hasAccount(state) {
    return state.account !== null;
  },
  getAccount(state) {
    return state.account;
  },
  getAccountUser(state) {
    return state.accountUser;
  },
  getAccountCanAccess(state) {
    return state.accountUser.canAccess ? state.accountUser.canAccess : undefined;
  },
  isAccountOwner(state) {
    return state.accountUser.permission === 'OWNER';
  },
  getApiKey(state) {
    return state.apiKey;
  },
  getBorgApiKey(state) {
    return state.borgApiKey;
  },
  getAppPermissions(state) {
    return state.appPermissions;
  },
  hasAppPermission(state) {
    return (appName) => {
      if (state.accountUser.canAccess && state.accountUser.canAccess[appName]) return true;
      return state.appPermissions.some(
        app => app.appName === appName
            && (app.status === AppStatus.ACTIVE || app.status === AppStatus.PENDING),
      ) || false;
    };
  },
  hasAppSubscription(state) {
    return appName => state.appPermissions.some(
      app => app.appName === appName
          && (app.status === AppStatus.ACTIVE || app.status === AppStatus.PENDING),
    ) || false;
  },
  hasIntegrationTokens(state) {
    return state.accountUser && state.accountUser.accessTokens
      ? state.accountUser.accessTokens
      : false;
  },
  hasToolkitPro(state) {
    return state.appPermissions.some(app => app.appName === 'TOOLKIT' && app.subscriptionLevel === 'PROFESSIONAL');
  },
  // Old Getters - converted to new state object
  hasActiveAccount(state) {
    return state.account !== null;
  },
  getActiveAccount(state) {
    return state.account;
  },
  getActiveAccountUser(state) {
    return state.accountUser;
  },
  getBillingDetails(state) {
    return state.account && state.account.billingDetails
      ? state.account.billingDetails
      : null;
  },
};

const state = {
  // Old
  activeAccount: null,
  activeAccountUser: null,
  accounts: [],
  // New
  apiKey: null,
  borgApiKey: null,
  accountUser: null,
  account: null,
  appPermissions: [],
};

const store = {
  namespaced: true,
  mutations,
  actions,
  getters,
  state,
};

export default store;
